import React from "react";
import { Box, Accordion, AccordionSummary, AccordionDetails, Chip, Typography, Stack, Tooltip, Link } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { TenantAction_ClaimsDbModel } from "@safelease/service-utilities";

interface TenantActionsAccordionProps {
  tenantActions: TenantAction_ClaimsDbModel[];
  onAccordionChange?: (isExpanded: boolean) => void;
}

/**
 * Displays pending tenant actions in an accordion
 * Shows the number of pending actions and provides help text/links
 */
function TenantActionsAccordion({ tenantActions, onAccordionChange }: TenantActionsAccordionProps) {
  const pendingActions = tenantActions.filter((action) => !action.completed);

  // Don't render if no pending actions
  if (pendingActions.length === 0) {
    return null;
  }

  // Format action type to be more readable
  const formatActionType = (type: string) => {
    const typeMap: Record<string, string> = {
      uploadProofOfOwnershipFiles: "Upload Proof of Ownership Files",
      uploadProofOfPurchaseFiles: "Upload Proof of Purchase Files",
      uploadClaimDocument: "Upload Claim Document",
      formQuestionAnswer: "Answer Required Question",
      settlementOffer: "Review Settlement Offer",
      adHocRequest: "Complete Adjuster Request",
      completeAdditionalLineItem: "Complete Additional Line Item",
    };
    return typeMap[type] || type;
  };

  // Tooltip content for tenant portal directions
  const tenantPortalTooltip = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Please direct the tenant to complete these actions in the tenant portal at{" "}
        <Link
          href="https://tenant.safelease.com"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: "white", textDecoration: "underline" }}
        >
          tenant.safelease.com
        </Link>
      </Typography>
      <Link
        href="https://safelease.webflow.io/support"
        target="_blank"
        rel="noopener noreferrer"
        sx={{ color: "white", textDecoration: "underline", fontSize: "0.875rem" }}
      >
        View FAQ →
      </Link>
    </Box>
  );

  return (
    <Accordion
      defaultExpanded={pendingActions.length > 0}
      onChange={(_, isExpanded) => onAccordionChange?.(isExpanded)}
      sx={{
        mb: 1,
        borderRadius: "8px !important",
        "&:before": { display: "none" },
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: "grey.50",
          borderRadius: "8px",
          "&.Mui-expanded": { minHeight: 48 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.primary" }}>
            Tenant Actions Needed
          </Typography>
          <Chip
            label={pendingActions.length}
            size="small"
            color="primary"
            sx={{
              fontWeight: 600,
            }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 2 }}>
        <Stack spacing={2}>
          {(() => {
            // Group actions by type and count them
            const groupedActions = pendingActions.reduce(
              (acc, action) => {
                const actionTitle = formatActionType(action.type);

                if (!acc[actionTitle]) {
                  acc[actionTitle] = 0;
                }

                acc[actionTitle] += 1;

                return acc;
              },
              {} as Record<string, number>,
            );

            // Render consolidated actions
            return Object.entries(groupedActions).map(([actionTitle, count], index) => (
              <Tooltip key={index} title={tenantPortalTooltip} arrow placement="top">
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    cursor: "help",
                    alignItems: "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      color: "#9E9E9E",
                      fontSize: "1.5rem",
                      lineHeight: 1,
                      flexShrink: 0,
                      mt: "-2px",
                    }}
                  >
                    •
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "text.primary",
                          lineHeight: 1.6,
                        }}
                      >
                        {actionTitle}
                      </Typography>
                      {count > 1 && (
                        <Chip
                          label={count}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            backgroundColor: "#e3f2fd",
                            color: "#0d47a1",
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Tooltip>
            ));
          })()}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default TenantActionsAccordion;
